
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, DollarSign, Clock, MapPin, User, Phone, FileText, CreditCard, Trash2, ShoppingBag, Truck, Hash, LayoutGrid, Info } from 'lucide-react';
import { Order } from '../types';

interface Props {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedOrder: Order) => void;
  onDelete: (id: string) => void;
}

export const EditOrderModal: React.FC<Props> = ({ order, isOpen, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState<Order | null>(null);
  const [deadlineTime, setDeadlineTime] = useState('');

  useEffect(() => {
    if (order) {
      setFormData({ ...order });

      if (order.createdAt && order.timeLimit) {
        const target = new Date(order.createdAt + order.timeLimit * 60000);
        const hh = String(target.getHours()).padStart(2, '0');
        const mm = String(target.getMinutes()).padStart(2, '0');
        setDeadlineTime(`${hh}:${mm}`);
      }
    }
  }, [order]);

  const handleChange = (field: keyof Order, value: any) => {
    if (formData) {
      setFormData({ ...formData, [field]: value });
    }
  };

  const toggleOrderType = () => {
    if (!formData) return;
    const newType = formData.type === 'TAKEOUT' ? 'DELIVERY' : 'TAKEOUT';
    if (newType === 'TAKEOUT') {
      setFormData({
        ...formData,
        type: 'TAKEOUT',
        deliveryFee: 0,
        address: 'Retirada em Balcão (Retirada)',
        streetAddress: 'Retirada na Loja',
        neighborhood: 'Balcão',
        complement: '',
        reference: ''
      });
    } else {
      setFormData({ ...formData, type: 'DELIVERY' });
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setDeadlineTime(newTime);

    if (formData && newTime && formData.createdAt) {
      const [hours, minutes] = newTime.split(':').map(Number);
      const created = new Date(formData.createdAt);
      const target = new Date(formData.createdAt);
      target.setHours(hours, minutes, 0, 0);

      if (target.getTime() < created.getTime() - 12 * 60 * 60 * 1000) {
        target.setDate(target.getDate() + 1);
      }

      const diffMs = target.getTime() - created.getTime();
      const diffMins = Math.round(diffMs / 60000);

      setFormData({ ...formData, timeLimit: diffMins });
    }
  };

  const handleSaveAndClose = () => {
    if (formData) {
      // Reconstroi o endereço completo para exibição no mapa e labels
      const fullAddress = formData.type === 'DELIVERY'
        ? [
          formData.streetAddress,
          formData.addressNumber ? `, ${formData.addressNumber}` : '',
          formData.complement ? ` - ${formData.complement}` : '',
          formData.neighborhood,
          formData.city,
          formData.state
        ].filter(Boolean).join(', ')
        : 'Retirada em Balcão (Takeout)';

      onSave({ ...formData, address: fullAddress });
    }
  }

  const handleDeleteAndClose = () => {
    if (formData && window.confirm('Tem certeza que deseja excluir este pedido?')) {
      onDelete(formData.id);
      onClose();
    }
  }

  if (!isOpen || !formData) return null;

  const inputClass = "w-full bg-input-bg border border-input-border rounded px-3 py-2 text-base text-text-primary font-medium focus:border-sal-cyber-teal focus:ring-1 focus:ring-sal-cyber-teal outline-none transition-colors placeholder-text-muted/50";

  // v41.7: Título com IDs corretos
  const titleId = formData.displayId ? `#${formData.displayId}` : (formData.shortCode ? `#${formData.shortCode}` : `#${formData.id.slice(-4)}`);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-overlay-rgba backdrop-blur-sm"
        />

        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="relative bg-card-bg w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-border-dim"
        >
          <div className="bg-app-bg text-text-primary px-6 py-4 flex justify-between items-center shrink-0 border-b border-border-dim">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                {formData.type === 'TAKEOUT' ? <ShoppingBag className="w-5 h-5 text-amber-500" /> : <Truck className="w-5 h-5 text-sal-cyber-teal" />}
                Editar Pedido {titleId}
              </h2>
              <p className="text-sm opacity-70 text-text-secondary uppercase tracking-widest font-black">
                {(formData.salesChannel || (formData.source === 'CARDAPIO_WEB' ? 'iFood' : formData.source) || 'Manual')} • {formData.type === 'TAKEOUT' ? 'RETIRADA' : 'ENTREGA'}
              </p>
            </div>
            <button type="button" onClick={onClose} aria-label="Fechar edição de pedido" className="p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-colors">
              <X className="w-6 h-6 text-text-muted" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">

            {/* Modalidade Toggle */}
            {formData.type === 'DELIVERY' && (
              <div className="flex gap-2 p-1 bg-input-bg rounded-xl border border-border-dim">
                <button
                  onClick={() => handleChange('type', 'DELIVERY')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-black uppercase transition-all ${formData.type === 'DELIVERY' ? 'bg-sal-cyber-teal text-white shadow-lg' : 'text-text-muted hover:text-text-primary'}`}
                >
                  <Truck className="w-4 h-4" /> Entrega
                </button>
                <button
                  onClick={toggleOrderType}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-black uppercase transition-all ${formData.type as any === 'TAKEOUT' ? 'bg-amber-500 text-white shadow-lg' : 'text-text-muted hover:text-text-primary'}`}
                >
                  <ShoppingBag className="w-4 h-4" /> Retirada
                </button>
              </div>
            )}

            {/* Identificação de Origem e IDs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center text-sm font-bold text-text-muted uppercase tracking-wide mb-1">
                  <LayoutGrid className="w-3 h-3 mr-1 text-sal-cyber-teal" /> Canal de Venda
                </label>
                <select
                  value={formData.salesChannel}
                  onChange={(e) => handleChange('salesChannel', e.target.value)}
                  className={inputClass}
                >
                  <option value="iFood">iFood</option>
                  <option value="Site Próprio">Site Próprio</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Rappi">Rappi</option>
                  <option value="Balcão">Balcão</option>
                  <option value="Telefone">Telefone</option>
                  {formData.salesChannel && !["iFood", "Site Próprio", "WhatsApp", "Rappi", "Balcão", "Telefone"].includes(formData.salesChannel) && (
                    <option value={formData.salesChannel}>{formData.salesChannel}</option>
                  )}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="flex items-center text-sm font-bold text-text-muted uppercase tracking-wide mb-1">
                    <Hash className="w-3 h-3 mr-1" /> ID Loja
                  </label>
                  <input
                    type="text"
                    value={formData.displayId || ''}
                    onChange={(e) => handleChange('displayId', e.target.value)}
                    className={`${inputClass} border-sal-cyber-teal/30 text-sal-cyber-teal`}
                    placeholder="#123"
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-bold text-text-muted uppercase tracking-wide mb-1">
                    <Hash className="w-3 h-3 mr-1" /> ID iFood
                  </label>
                  <input
                    type="text"
                    value={formData.shortCode || ''}
                    onChange={(e) => handleChange('shortCode', e.target.value)}
                    className={`${inputClass} border-rose-500/30 text-rose-400`}
                    placeholder="#4567"
                  />
                </div>
              </div>
            </div>

            {/* v47.1: ID de Validação iFood (Extraído da Observação) */}
            {(formData.salesChannel === 'iFood' || formData.salesChannel === 'Open Delivery') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-bold text-status-critical uppercase tracking-wide mb-1">
                    <Hash className="w-3 h-3 mr-1" /> ID Validação (Obs)
                  </label>
                  <input
                    type="text"
                    value={formData.ifoodSupportId || ''}
                    onChange={(e) => handleChange('ifoodSupportId', e.target.value)}
                    className={`${inputClass} border-status-critical/30 text-status-critical placeholder-text-muted/20`}
                    placeholder="8 dígitos"
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-bold text-status-critical uppercase tracking-wide mb-1">
                    <Phone className="w-3 h-3 mr-1" /> Tel Proxy
                  </label>
                  <input
                    type="text"
                    value={formData.customerProxyPhone || ''}
                    onChange={(e) => handleChange('customerProxyPhone', e.target.value)}
                    className={`${inputClass} border-status-critical/30 text-status-critical placeholder-text-muted/20`}
                    placeholder="0800..."
                  />
                </div>
              </div>
            )}

            {/* ID Único Interno (apenas visualização) */}
            <div className="p-2 bg-black/5 dark:bg-white/5 rounded border border-border-dim flex items-center justify-between">
              <span className="text-xs uppercase font-bold text-text-muted">ID Único (Interno):</span>
              <span className="text-xs font-mono text-text-muted opacity-60">{formData.id}</span>
            </div>

            {/* Dados do Cliente */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center text-sm font-bold text-text-muted uppercase tracking-wide mb-1">
                  <User className="w-3 h-3 mr-1" /> Nome do Cliente
                </label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => handleChange('customerName', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-bold text-text-muted uppercase tracking-wide mb-1">
                  <Phone className="w-3 h-3 mr-1" /> Telefone
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Configuração de Tempo */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="flex items-center text-sm font-bold text-text-muted uppercase tracking-wide mb-1">
                  <Clock className="w-3 h-3 mr-1" /> Prazo Final de Entrega
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={deadlineTime}
                    onChange={handleTimeChange}
                    className={`${inputClass} flex-1 text-center font-mono`}
                  />
                  <div className="bg-black/5 dark:bg-white/5 px-4 py-2 rounded text-sm font-mono font-black text-sal-cyber-teal border border-border-dim min-w-[100px] text-center">
                    {formData.timeLimit} MIN
                  </div>
                </div>
              </div>
            </div>

            {/* Seção de Endereço (Cardápio Web Style) */}
            {formData.type === 'DELIVERY' ? (
              <div className="space-y-4 p-4 bg-app-bg/50 rounded-lg border border-border-dim shadow-inner">
                <label className="flex items-center text-base font-bold text-text-primary mb-2">
                  <MapPin className="w-4 h-4 mr-2 text-sal-cyber-teal" /> Localização Detalhada
                </label>

                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-3">
                    <label className="text-xs font-bold text-text-muted uppercase tracking-widest mb-1 block">Logradouro</label>
                    <input type="text" value={formData.streetAddress} onChange={e => handleChange('streetAddress', e.target.value)} className={`${inputClass} bg-input-bg/50`} placeholder="Rua..." />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-text-muted uppercase tracking-widest mb-1 block">Número</label>
                    <input type="text" value={formData.addressNumber || ''} onChange={e => handleChange('addressNumber', e.target.value)} className={`${inputClass} bg-input-bg/50`} placeholder="123" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-text-muted uppercase tracking-widest mb-1 block">Complemento</label>
                    <input type="text" value={formData.complement || ''} onChange={e => handleChange('complement', e.target.value)} className={`${inputClass} bg-input-bg/50`} placeholder="Apto 101, Bloco B..." />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-text-muted uppercase tracking-widest mb-1 block">Ponto de Referência</label>
                    <input type="text" value={formData.reference || ''} onChange={e => handleChange('reference', e.target.value)} className={`${inputClass} bg-input-bg/50`} placeholder="Próximo ao mercado..." />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-bold text-text-muted uppercase tracking-widest mb-1 block">Bairro</label>
                    <input type="text" value={formData.neighborhood} onChange={e => handleChange('neighborhood', e.target.value)} className={`${inputClass} bg-input-bg/50`} />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-text-muted uppercase tracking-widest mb-1 block">Cidade</label>
                    <input type="text" value={formData.city} onChange={e => handleChange('city', e.target.value)} className={`${inputClass} bg-input-bg/50`} />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-text-muted uppercase tracking-widest mb-1 block">UF</label>
                    <input type="text" value={formData.state} onChange={e => handleChange('state', e.target.value)} className={`${inputClass} bg-input-bg/50`} maxLength={2} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 bg-amber-900/10 border-2 border-dashed border-amber-500/20 rounded-2xl flex flex-col items-center text-center gap-3">
                <ShoppingBag className="w-10 h-10 text-amber-500/50" />
                <div>
                  <h4 className="text-base font-black text-amber-500 uppercase">Pedido para Retirada</h4>
                  <p className="text-sm text-text-secondary font-medium">O cliente virá buscar o pedido no balcão da loja. Endereço e taxa de entrega desativados.</p>
                </div>
              </div>
            )}

            {/* Detalhes do Pedido */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center text-sm font-bold text-text-muted uppercase tracking-wide mb-1">
                  <FileText className="w-3 h-3 mr-1" /> Itens do Pedido
                </label>
                <textarea
                  value={formData.items}
                  onChange={(e) => handleChange('items', e.target.value)}
                  rows={3}
                  className={`${inputClass} resize-none`}
                />
              </div>
              <div className="space-y-4">
                <div>
                  <label className="flex items-center text-sm font-bold text-text-muted uppercase tracking-wide mb-1">
                    <CreditCard className="w-3 h-3 mr-1" /> Forma de Pagamento
                  </label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => handleChange('paymentMethod', e.target.value)}
                    className={inputClass}
                  >
                    <optgroup label="Pago Antecipado">
                      <option value="Pago Online (App)">Pago Online (iFood/App)</option>
                      <option value="Pix (Automático)">Pix (Automático / Site)</option>
                      <option value="Crédito Online">Crédito Online (Site)</option>
                    </optgroup>
                    <optgroup label="Pagar no Balcão / Entrega">
                      <option value="Pix (Cobrar)">Pix</option>
                      <option value="Dinheiro (Cobrar)">Dinheiro</option>
                      <option value="Maquininha (Cobrar)">Maquininha</option>
                    </optgroup>
                    {formData.paymentMethod && !["Pago Online (App)", "Pix (Automático)", "Crédito Online", "Pix (Cobrar)", "Dinheiro (Cobrar)", "Maquininha (Cobrar)"].includes(formData.paymentMethod) && (
                      <option value={formData.paymentMethod}>{formData.paymentMethod}</option>
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* Seção Financeira */}
            <div className="bg-app-bg/50 p-4 rounded-lg border border-border-dim">
              <h3 className="text-base font-bold text-text-primary mb-3 flex items-center">
                <DollarSign className="w-4 h-4 mr-1 text-sal-gold" /> Ajuste Financeiro
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-text-muted uppercase mb-1">Taxa Entrega (R$)</label>
                  <input
                    type="number"
                    step="0.10"
                    disabled={formData.type === 'TAKEOUT'}
                    value={formData.deliveryFee ?? ''}
                    onChange={(e) => handleChange('deliveryFee', parseFloat(e.target.value) || 0)}
                    className={`${inputClass} font-mono font-bold text-text-primary bg-input-bg/50 disabled:opacity-30`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-text-muted uppercase mb-1">Cotação App (R$)</label>
                  <input
                    type="number"
                    step="0.10"
                    disabled={formData.type === 'TAKEOUT'}
                    value={formData.costThirdParty}
                    onChange={(e) => handleChange('costThirdParty', parseFloat(e.target.value) || 0)}
                    className={`${inputClass} font-mono font-bold text-text-primary bg-input-bg/50 disabled:opacity-30`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-text-muted uppercase mb-1">Valor Total (R$)</label>
                  <input
                    type="number"
                    step="0.10"
                    value={formData.orderValue ?? ''}
                    onChange={(e) => handleChange('orderValue', parseFloat(e.target.value) || 0)}
                    className={`${inputClass} font-mono font-bold text-status-ready bg-input-bg/50 border-status-ready/30`}
                  />
                </div>
              </div>
            </div>

          </div>

          <div className="p-4 border-t border-border-dim bg-app-bg flex justify-between items-center shrink-0">
            <button
              onClick={handleDeleteAndClose}
              className="px-4 py-2 text-base font-bold text-red-500 hover:bg-red-900/20 rounded transition-colors flex items-center"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Excluir Pedido
            </button>

            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-base font-medium text-text-muted hover:text-text-primary hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveAndClose}
                className="px-6 py-2 bg-sal-cyber-teal text-white dark:text-sal-gunmetal text-base font-bold rounded shadow hover:brightness-110 transition-colors flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Salvar Alterações
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
